import React, { FunctionComponent } from 'react';
import { Progress, Icon } from 'semantic-ui-react';
import withStyles from 'react-jss';
import { FormattedNumber } from 'react-intl';
import classnames from 'classnames';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { pathOr, propOr, isEmpty } from 'ramda';
import { useQuery } from 'react-apollo';

import { ClassProp, WithRouterProps } from 'helpers/types';
import { abbreviateNumber } from 'helpers/stringFunctions';
import { GET_PROFILE } from 'components/UserMgmt/UserMgmt.queries';
import Query from 'components/Query';

import styles from './Budgets.styles';
import GET_CAMPAIGN_BUDGET from './Budgets.queries';

type Props = WithRouterProps & {
  classes: ClassProp<typeof styles>;
};

const calculateCampaignValues = ({ budgets }: any) => {
  if (isEmpty(budgets)) {
    return {
      pctBudgetRemaining: 0,
      remainingBudgetAmt: 0,
      campaignBudgetAmt: 0,
    };
  }

  // working off the notion that we only set up one budget per campaign
  const { totalBudget, balance, type } = budgets[0];
  const currBalance = balance <= 0 || type === 'OPEN' ? totalBudget : balance;

  const pctBudgetRemaining = 100 * (currBalance / totalBudget);
  return {
    type,
    pctBudgetRemaining,
    remainingBudgetAmt: currBalance,
    campaignBudgetAmt: totalBudget,
  };
};

const progressBarStatus = (percentRemaining: number) => {
  if (percentRemaining === 100) return 'completed';
  if (percentRemaining >= 60) return 'success';
  if (percentRemaining < 30) return 'error';
  if (percentRemaining >= 30 && percentRemaining < 60) return 'warning';
  return null;
};

const getFormattedValue = (
  remainingBudgetAmt: number,
  campaignBudgetAmt: number,
) =>
  `$${abbreviateNumber(remainingBudgetAmt, 0)} of $${abbreviateNumber(
    campaignBudgetAmt,
    0,
  )}`;

const renderBudget = (classes: ClassProp<typeof styles>) => ({
  data,
  loading,
}: any) => {
  if (loading) {
    return (
      <div className={classes.grid}>
        <Icon name="spinner" loading />
      </div>
    );
  }

  const campaigns: [] = propOr([], 'campaignFindBy', data);
  if (isEmpty(campaigns)) return null;

  const activeCampaign = campaigns.reduce((curr, prev) => {
    const startDate = pathOr('', ['campaign', 'startDate'], curr);
    const startDate2 = pathOr('', ['campaign', 'startDate'], prev);

    return startDate2 > startDate ? prev : curr;
  });

  const {
    type,
    campaignBudgetAmt,
    pctBudgetRemaining,
    remainingBudgetAmt,
  } = calculateCampaignValues(activeCampaign);
  const progressBar = progressBarStatus(pctBudgetRemaining);

  return (
    <div className={classes.grid}>
      <div className={classes.label}>BUDGET REMAINING</div>

      <div className={classes.secondaryData}>
        {getFormattedValue(remainingBudgetAmt, campaignBudgetAmt)}
        &nbsp;
        {type === 'OPEN' ? (
          `(OPEN)`
        ) : (
          <>
            (
            <FormattedNumber
              value={pctBudgetRemaining || 0}
              maximumFractionDigits={1}
            />{' '}
            %)
          </>
        )}
      </div>

      <Progress
        percent={pctBudgetRemaining}
        size="tiny"
        className={classnames(classes.progressBar, progressBar)}
      />
    </div>
  );
};

const Budget: FunctionComponent<Props> = ({ classes, match }) => {
  const profilePayload = useQuery(GET_PROFILE);
  const { userProfile } = profilePayload.data;
  const params = {
    advertiserMerchantUuid: pathOr(
      userProfile.merchantId,
      ['params', 'merchantId'],
      match,
    ),
  };

  return (
    <Query
      enableProgressInfo
      query={GET_CAMPAIGN_BUDGET}
      render={renderBudget(classes)}
      variables={{
        params,
      }}
    />
  );
};

export default compose<Props, any>(withRouter, withStyles(styles))(Budget);
